import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectPuttingCloseAccountFromState,
  selectTopLevelErrorFromState,
} from 'src/app/CloseAccountContent/redux/closeAccountContent.selectors'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import SvgCloseCard from 'src/components/Svgs/SvgCloseCard'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { CONFIRM_CLOSE_CARD_TEXT, ConfirmCloseCardText } from 'src/constants'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { putCloseAccountAndCloseModal } from 'src/redux/actions/data/creditCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface CloseAccountContentProps {
  creditCardId: string
  creditCard: CreditCardListModel | undefined
}

const CloseAccountContent: React.FC<CloseAccountContentProps> = ({
  creditCardId,
  creditCard,
}): React.ReactElement => {
  const translate = useTranslate()

  const [confirmationText, setConfirmationText] = useState<string | undefined>()

  const dispatch = useDispatch()
  const history = useHistory()

  // TODO: React Query
  const closing = useSelector(selectPuttingCloseAccountFromState)
  const error = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)

  const isConfirmationFormValid =
    confirmationText === ConfirmCloseCardText[locale]

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (creditCardId && isConfirmationFormValid) {
      dispatch(
        putCloseAccountAndCloseModal(
          creditCardId,
          creditCard?.displayName || '',
        ),
      )
      analytics?.track('confirm close account')
    }
  }

  return (
    <Popup
      css={css`
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
    >
      {creditCard !== undefined && !creditCard.isClosed ? (
        <ModalDialogForm
          formOnSubmit={handleFormSubmit}
          actionButtonLabel={
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <SvgCloseCard
                fill={styleConstants.colors.gray_lightest}
                css={css`
                  transform: scale(calc(16 / 24));
                `}
              />
              {translate('Confirm and Close Account')}
            </span>
          }
          actionButtonLoading={closing}
          actionButtonVariant="severe"
          cancelButtonLabel={translate('Cancel')}
          cancelButtonOnClick={(): void => {
            history.goBack()
          }}
          showCancelAsLink
          isFormValid={isConfirmationFormValid}
          error={error}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 32px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >
              {translate('Close Account')}
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                'Close ${cardName}? This will close your account permanently. This will also permanently deactivate any budgets and close any virtual cards under this account.',
                {
                  cardName: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {creditCard?.displayName}
                    </span>
                  ),
                },
              )}
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                'You can still view previously created budgets, virtual cards and transactions, but ${bolded}.',
                {
                  bolded: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {translate(
                        'you cannot reactivate this account in the future',
                      )}
                    </span>
                  ),
                },
              )}
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 40px;
                width: 100%;
              `}
            >
              <TextInputField
                allCaps
                design={TextInputFieldDesigns.New}
                name="remove-confirmation"
                fieldLabel={
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('Confirm by typing ${close} below:', {
                      close: (
                        <span style={{ color: `${styleConstants.colors.red}` }}>
                          {translate(`"${CONFIRM_CLOSE_CARD_TEXT}"`)}
                        </span>
                      ),
                    })}
                  </span>
                }
                placeholder={translateSingleString(
                  locale,
                  'Please type "CLOSE"',
                )}
                onChange={(value: string): void =>
                  setConfirmationText(value.toUpperCase())
                }
              />
            </span>
          </div>
        </ModalDialogForm>
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default CloseAccountContent
